<div class="help-page">
  <div class="help-container">
    <!-- SECTIONS NAVIGATION -->
    <nav class="sections-nav">
      <div class="nav-title">Sections</div>
      <ul class="nav-list">
        <li class="nav-item">
          <a
            href="#getting-started"
            class="nav-link"
            [class.active]="isSectionExpanded('getting-started')"
            (click)="
              navigateToSection('getting-started'); $event.preventDefault()
            "
            >Getting Started</a
          >
        </li>
        <li class="nav-item">
          <a
            href="#production-pipeline"
            class="nav-link"
            [class.active]="isSectionExpanded('production-pipeline')"
            (click)="
              navigateToSection('production-pipeline'); $event.preventDefault()
            "
            >Production Pipeline</a
          >
        </li>
        <li class="nav-item">
          <a
            href="#controls-approvals"
            class="nav-link"
            [class.active]="isSectionExpanded('controls-approvals')"
            (click)="
              navigateToSection('controls-approvals'); $event.preventDefault()
            "
            >Controls & Approvals</a
          >
        </li>
        <li class="nav-item">
          <a
            href="#assets-ownership"
            class="nav-link"
            [class.active]="isSectionExpanded('assets-ownership')"
            (click)="
              navigateToSection('assets-ownership'); $event.preventDefault()
            "
            >Assets & Ownership</a
          >
        </li>
        <li class="nav-item">
          <a
            href="#organization-collaboration"
            class="nav-link"
            [class.active]="isSectionExpanded('organization-collaboration')"
            (click)="
              navigateToSection('organization-collaboration');
              $event.preventDefault()
            "
            >Organization & Collaboration</a
          >
        </li>
        <li class="nav-item">
          <a
            href="#troubleshooting"
            class="nav-link"
            [class.active]="isSectionExpanded('troubleshooting')"
            (click)="
              navigateToSection('troubleshooting'); $event.preventDefault()
            "
            >Troubleshooting & FAQs</a
          >
        </li>
        <li class="nav-item">
          <a
            href="#security-transparency"
            class="nav-link"
            [class.active]="isSectionExpanded('security-transparency')"
            (click)="
              navigateToSection('security-transparency');
              $event.preventDefault()
            "
            >Security & AI Transparency</a
          >
        </li>
        <li class="nav-item">
          <a
            href="#support-contact"
            class="nav-link"
            [class.active]="isSectionExpanded('support-contact')"
            (click)="
              navigateToSection('support-contact'); $event.preventDefault()
            "
            >Support & Contact</a
          >
        </li>
      </ul>
    </nav>

    <!-- HELP CONTENT -->
    <div class="help-content">
      <!-- SECTION 1: GETTING STARTED -->
      <section
        class="accordion-section"
        [class.expanded]="isSectionExpanded('getting-started')"
        id="getting-started"
      >
        <div
          class="accordion-header"
          (click)="toggleSection('getting-started')"
        >
          <div class="accordion-header-content">
            <h2 class="accordion-title">
              <span class="section-number">01</span>
              Getting Started
            </h2>
            <p class="accordion-description">
              Understand dAIrector's core concepts and typical first-time flow
            </p>
          </div>
          <div class="accordion-icon">
            <app-icon name="chevron-down" size="16"></app-icon>
          </div>
        </div>
        @if (isSectionExpanded("getting-started")) {
          <div class="accordion-content">
            <div class="content-block">
              <h3>What is dAIrector?</h3>
              <p>
                dAIrector is a production orchestration platform that transforms
                creative intent into professional-grade video outputs using a
                structured, multi-stage pipeline. It replaces ad‑hoc prompting
                with deterministic workflows inspired by real-world studio
                production.
              </p>

              <h3>Core Concepts</h3>
              <ul class="content-list">
                <li>
                  <strong>Organization</strong> – Your workspace and unit of
                  ownership
                </li>
                <li>
                  <strong>Campaign</strong> – A strategic container that ensures
                  consistency across related videos
                </li>
                <li>
                  <strong>Video Project</strong> – A single production unit
                  processed through the pipeline
                </li>
                <li>
                  <strong>Pipeline Stages</strong> – The ordered steps that
                  convert intent into final media
                </li>
              </ul>

              <h3>Typical First-Time Flow</h3>
              <ol class="numbered-list">
                <li>Create or join an Organization</li>
                <li>Define a Campaign</li>
                <li>Start a Video Project</li>
                <li>Progress through the production pipeline</li>
                <li>Approve outputs and export final assets</li>
              </ol>
            </div>
          </div>
        }
      </section>

      <!-- SECTION 2: PRODUCTION PIPELINE -->
      <section
        class="accordion-section"
        [class.expanded]="isSectionExpanded('production-pipeline')"
        id="production-pipeline"
      >
        <div
          class="accordion-header"
          (click)="toggleSection('production-pipeline')"
        >
          <div class="accordion-header-content">
            <h2 class="accordion-title">
              <span class="section-number">02</span>
              The Production Pipeline
            </h2>
            <p class="accordion-description">
              Understand each stage of video production and its purpose
            </p>
          </div>
          <div class="accordion-icon">
            <app-icon name="chevron-down" size="16"></app-icon>
          </div>
        </div>
        @if (isSectionExpanded("production-pipeline")) {
          <div class="accordion-content">
            <div class="content-block">
              <p>
                Each video moves through a fixed sequence of stages. Every stage
                has a clear purpose, defined inputs, and specific outputs.
              </p>

              <div class="pipeline-stages">
                <!-- Stage 1 -->
                <div
                  class="stage-accordion"
                  [class.expanded]="isStageExpanded('stage-1')"
                >
                  <div
                    class="stage-header"
                    (click)="toggleStage('stage-1', $event)"
                  >
                    <div class="stage-header-content">
                      <span class="stage-number">1</span>
                      <span class="stage-title">Concept Manifest</span>
                    </div>
                    <div class="stage-icon">
                      <app-icon name="chevron-down" size="14"></app-icon>
                    </div>
                  </div>
                  @if (isStageExpanded("stage-1")) {
                    <div class="stage-content">
                      <p>
                        Defines the strategic and creative blueprint for the
                        video. Decisions made here guide all downstream stages.
                      </p>
                      <div class="key-points">
                        <div class="key-points-label">Key Points</div>
                        <ul class="key-points-list">
                          <li>
                            Establishes intent, structure, and constraints
                          </li>
                          <li>
                            Changes later may require re-running subsequent
                            stages
                          </li>
                        </ul>
                      </div>
                    </div>
                  }
                </div>

                <!-- Stage 2 -->
                <div
                  class="stage-accordion"
                  [class.expanded]="isStageExpanded('stage-2')"
                >
                  <div
                    class="stage-header"
                    (click)="toggleStage('stage-2', $event)"
                  >
                    <div class="stage-header-content">
                      <span class="stage-number">2</span>
                      <span class="stage-title">Script</span>
                    </div>
                    <div class="stage-icon">
                      <app-icon name="chevron-down" size="14"></app-icon>
                    </div>
                  </div>
                  @if (isStageExpanded("stage-2")) {
                    <div class="stage-content">
                      <p>
                        Generates or structures the narrative based on the
                        approved Manifest.
                      </p>
                      <div class="key-points">
                        <div class="key-points-label">Key Points</div>
                        <ul class="key-points-list">
                          <li>
                            Timing and structure are locked to the Manifest
                          </li>
                          <li>
                            Script approvals influence all visual execution
                          </li>
                        </ul>
                      </div>
                    </div>
                  }
                </div>

                <!-- Stage 3 -->
                <div
                  class="stage-accordion"
                  [class.expanded]="isStageExpanded('stage-3')"
                >
                  <div
                    class="stage-header"
                    (click)="toggleStage('stage-3', $event)"
                  >
                    <div class="stage-header-content">
                      <span class="stage-number">3</span>
                      <span class="stage-title"
                        >Moodboard & Entity Standardization</span
                      >
                    </div>
                    <div class="stage-icon">
                      <app-icon name="chevron-down" size="14"></app-icon>
                    </div>
                  </div>
                  @if (isStageExpanded("stage-3")) {
                    <div class="stage-content">
                      <p>
                        Locks visual identity such as characters, locations, and
                        stylistic rules.
                      </p>
                      <div class="key-points">
                        <div class="key-points-label">Key Points</div>
                        <ul class="key-points-list">
                          <li>Prevents identity drift</li>
                          <li>
                            Approved entities remain consistent across outputs
                          </li>
                        </ul>
                      </div>
                    </div>
                  }
                </div>

                <!-- Stage 4 -->
                <div
                  class="stage-accordion"
                  [class.expanded]="isStageExpanded('stage-4')"
                >
                  <div
                    class="stage-header"
                    (click)="toggleStage('stage-4', $event)"
                  >
                    <div class="stage-header-content">
                      <span class="stage-number">4</span>
                      <span class="stage-title">Shotlist & Storyboard</span>
                    </div>
                    <div class="stage-icon">
                      <app-icon name="chevron-down" size="14"></app-icon>
                    </div>
                  </div>
                  @if (isStageExpanded("stage-4")) {
                    <div class="stage-content">
                      <p>Maps the visual plan for execution.</p>
                      <div class="key-points">
                        <div class="key-points-label">Key Points</div>
                        <ul class="key-points-list">
                          <li>Defines framing, sequencing, and pacing</li>
                          <li>Acts as the blueprint for video generation</li>
                        </ul>
                      </div>
                    </div>
                  }
                </div>

                <!-- Stage 5 -->
                <div
                  class="stage-accordion"
                  [class.expanded]="isStageExpanded('stage-5')"
                >
                  <div
                    class="stage-header"
                    (click)="toggleStage('stage-5', $event)"
                  >
                    <div class="stage-header-content">
                      <span class="stage-number">5</span>
                      <span class="stage-title">Cinematic Execution</span>
                    </div>
                    <div class="stage-icon">
                      <app-icon name="chevron-down" size="14"></app-icon>
                    </div>
                  </div>
                  @if (isStageExpanded("stage-5")) {
                    <div class="stage-content">
                      <p>
                        Generates high‑fidelity video clips based on the
                        approved storyboard.
                      </p>
                      <div class="key-points">
                        <div class="key-points-label">Key Points</div>
                        <ul class="key-points-list">
                          <li>Outputs may have controlled variations</li>
                          <li>
                            Approved clips are treated as final unless
                            explicitly reopened
                          </li>
                        </ul>
                      </div>
                    </div>
                  }
                </div>

                <!-- Stage 6 -->
                <div
                  class="stage-accordion"
                  [class.expanded]="isStageExpanded('stage-6')"
                >
                  <div
                    class="stage-header"
                    (click)="toggleStage('stage-6', $event)"
                  >
                    <div class="stage-header-content">
                      <span class="stage-number">6</span>
                      <span class="stage-title">Sonic Layer</span>
                    </div>
                    <div class="stage-icon">
                      <app-icon name="chevron-down" size="14"></app-icon>
                    </div>
                  </div>
                  @if (isStageExpanded("stage-6")) {
                    <div class="stage-content">
                      <p>
                        Applies voiceover, background music, and sound design.
                      </p>
                      <div class="key-points">
                        <div class="key-points-label">Key Points</div>
                        <ul class="key-points-list">
                          <li>Aligned to emotional and timing metadata</li>
                          <li>Final layer before export</li>
                        </ul>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </section>

      <!-- SECTION 3: CONTROLS & APPROVALS -->
      <section
        class="accordion-section"
        [class.expanded]="isSectionExpanded('controls-approvals')"
        id="controls-approvals"
      >
        <div
          class="accordion-header"
          (click)="toggleSection('controls-approvals')"
        >
          <div class="accordion-header-content">
            <h2 class="accordion-title">
              <span class="section-number">03</span>
              Controls, Approvals & Responsibility
            </h2>
            <p class="accordion-description">
              How approvals work and what they mean
            </p>
          </div>
          <div class="accordion-icon">
            <app-icon name="chevron-down" size="16"></app-icon>
          </div>
        </div>
        @if (isSectionExpanded("controls-approvals")) {
          <div class="accordion-content">
            <div class="content-block">
              <p>Approvals are deliberate control points in dAIrector.</p>

              <ul class="content-list">
                <li>
                  Approving a stage confirms that its output is accepted as the
                  basis for downstream work
                </li>
                <li>
                  Approved stages may restrict edits to preserve consistency
                </li>
                <li>
                  Autonomous and semi‑autonomous modes automate execution but do
                  not shift responsibility
                </li>
              </ul>

              <div class="emphasis-box">
                <p>
                  <strong
                    >Final responsibility for review, approval, and usage of
                    outputs remains with the user or Organization.</strong
                  >
                </p>
              </div>
            </div>
          </div>
        }
      </section>

      <!-- SECTION 4: ASSETS & OWNERSHIP -->
      <section
        class="accordion-section"
        [class.expanded]="isSectionExpanded('assets-ownership')"
        id="assets-ownership"
      >
        <div
          class="accordion-header"
          (click)="toggleSection('assets-ownership')"
        >
          <div class="accordion-header-content">
            <h2 class="accordion-title">
              <span class="section-number">04</span>
              Assets, Ownership & Exports
            </h2>
            <p class="accordion-description">
              What you own and how to export your work
            </p>
          </div>
          <div class="accordion-icon">
            <app-icon name="chevron-down" size="16"></app-icon>
          </div>
        </div>
        @if (isSectionExpanded("assets-ownership")) {
          <div class="accordion-content">
            <div class="content-block">
              <h3>Asset Types</h3>
              <ul class="content-list">
                <li>
                  Draft and intermediate assets (scripts, storyboards, previews)
                </li>
                <li>Final video outputs and audio assets</li>
              </ul>

              <h3>Ownership</h3>
              <div class="emphasis-box">
                <p>
                  <strong
                    >Users retain full ownership of all final outputs, including
                    exclusive commercial rights.</strong
                  >
                </p>
              </div>

              <h3>Exports</h3>
              <ul class="content-list">
                <li>Final assets can be downloaded in supported formats</li>
                <li>Re-exports are available for approved outputs</li>
              </ul>
            </div>
          </div>
        }
      </section>

      <!-- SECTION 5: ORGANIZATION & COLLABORATION -->
      <section
        class="accordion-section"
        [class.expanded]="isSectionExpanded('organization-collaboration')"
        id="organization-collaboration"
      >
        <div
          class="accordion-header"
          (click)="toggleSection('organization-collaboration')"
        >
          <div class="accordion-header-content">
            <h2 class="accordion-title">
              <span class="section-number">05</span>
              Account, Organization & Collaboration
            </h2>
            <p class="accordion-description">
              Managing access and working with teams
            </p>
          </div>
          <div class="accordion-icon">
            <app-icon name="chevron-down" size="16"></app-icon>
          </div>
        </div>
        @if (isSectionExpanded("organization-collaboration")) {
          <div class="accordion-content">
            <div class="content-block">
              <ul class="content-list">
                <li>Organizations control access and permissions</li>
                <li>Members may have different roles and capabilities</li>
                <li>
                  Organization owners are responsible for all activity within
                  the workspace
                </li>
              </ul>

              <div class="emphasis-box">
                <p>
                  If a collaborator leaves an Organization, access to its assets
                  is revoked.
                </p>
              </div>
            </div>
          </div>
        }
      </section>

      <!-- SECTION 6: TROUBLESHOOTING & FAQS -->
      <section
        class="accordion-section"
        [class.expanded]="isSectionExpanded('troubleshooting')"
        id="troubleshooting"
      >
        <div
          class="accordion-header"
          (click)="toggleSection('troubleshooting')"
        >
          <div class="accordion-header-content">
            <h2 class="accordion-title">
              <span class="section-number">06</span>
              Troubleshooting & FAQs
            </h2>
            <p class="accordion-description">Answers to common questions</p>
          </div>
          <div class="accordion-icon">
            <app-icon name="chevron-down" size="16"></app-icon>
          </div>
        </div>
        @if (isSectionExpanded("troubleshooting")) {
          <div class="accordion-content">
            <div class="content-block">
              <div class="faq-item">
                <div class="faq-question">Why is a stage locked?</div>
                <div class="faq-answer">
                  A stage may be locked because a downstream stage has already
                  been approved.
                </div>
              </div>

              <div class="faq-item">
                <div class="faq-question">Why does output vary slightly?</div>
                <div class="faq-answer">
                  Some stages allow controlled variation within defined
                  constraints.
                </div>
              </div>

              <div class="faq-item">
                <div class="faq-question">
                  What happens if generation fails?
                </div>
                <div class="faq-answer">
                  The system may retry automatically or request user
                  intervention.
                </div>
              </div>

              <div class="faq-item">
                <div class="faq-question">
                  Why can't I edit an approved asset?
                </div>
                <div class="faq-answer">
                  Approved assets are frozen to maintain deterministic outcomes.
                </div>
              </div>
            </div>
          </div>
        }
      </section>

      <!-- SECTION 7: SECURITY & AI TRANSPARENCY -->
      <section
        class="accordion-section"
        [class.expanded]="isSectionExpanded('security-transparency')"
        id="security-transparency"
      >
        <div
          class="accordion-header"
          (click)="toggleSection('security-transparency')"
        >
          <div class="accordion-header-content">
            <h2 class="accordion-title">
              <span class="section-number">07</span>
              Security, Data & AI Transparency
            </h2>
            <p class="accordion-description">
              How we handle your data and use AI
            </p>
          </div>
          <div class="accordion-icon">
            <app-icon name="chevron-down" size="16"></app-icon>
          </div>
        </div>
        @if (isSectionExpanded("security-transparency")) {
          <div class="accordion-content">
            <div class="content-block">
              <ul class="content-list">
                <li>User data is isolated per Organization</li>
                <li>Inputs and outputs are not used to train AI models</li>
                <li>
                  dAIrector uses third‑party AI systems at a high level to
                  enable functionality
                </li>
              </ul>

              <p style="margin-top: var(--space-4)">
                Refer to the
                <a
                  routerLink="/layout/privacy-policy"
                  style="color: var(--electric-cyan)"
                  >Privacy Policy</a
                >
                and
                <a
                  routerLink="/layout/terms-of-service"
                  style="color: var(--electric-cyan)"
                  >Terms of Service</a
                >
                for formal details.
              </p>
            </div>
          </div>
        }
      </section>

      <!-- SECTION 8: SUPPORT & CONTACT -->
      <section
        class="accordion-section"
        [class.expanded]="isSectionExpanded('support-contact')"
        id="support-contact"
      >
        <div
          class="accordion-header"
          (click)="toggleSection('support-contact')"
        >
          <div class="accordion-header-content">
            <h2 class="accordion-title">
              <span class="section-number">08</span>
              Support & Contact
            </h2>
            <p class="accordion-description">
              How to get help when you need it
            </p>
          </div>
          <div class="accordion-icon">
            <app-icon name="chevron-down" size="16"></app-icon>
          </div>
        </div>
        @if (isSectionExpanded("support-contact")) {
          <div class="accordion-content">
            <div class="content-block">
              <p>If you need assistance:</p>
              <ul class="content-list">
                <li>Use in‑app support where available</li>
                <li>
                  Contact the dAIrector team for account or platform issues
                </li>
              </ul>

              <div class="emphasis-box">
                <p>
                  Support covers platform usage and system behavior. Creative or
                  legal review of outputs is the user's responsibility.
                </p>
              </div>
            </div>
          </div>
        }
      </section>
    </div>
  </div>
</div>
